import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import {
  CATEGORY_META, FILTER_ALL, SCOPE,
} from '../../../shared/constants';
import { triggerBrowserDownload } from '../../../shared/utils/download';
import { activityDisplayName } from '../../../shared/utils/domain';
import { managementReportingYearOptions } from '../../../shared/utils/reportingYear';
import createIdempotencyKey from '../../../shared/utils/idempotency';
import pollKnownOperationStatus from '../../../shared/utils/pollKnownOperationStatus';
import { matchesSearch } from '../../../shared/utils/text';
import { requireEfSyncStatusUrl } from '../../../shared/api/operationStatusUrl';
import {
  createEmissionFactor,
  downloadSourceFile,
  getEmissionFactorHistory,
  getEmissionFactorSyncPolicy,
  getEmissionFactorSyncStatus,
  listEmissionFactorSyncHistory,
  listEmissionFactors,
  publishCorrection,
  syncEmissionFactors,
  updateEmissionFactorStatus,
  uploadSourceFile,
} from '../../../api/emissionFactorsApi';
import { listElectricitySuppliers } from '../../../api/officesApi';
import {
  getAuthenticatedUserId,
  useAuthContext,
} from '../../../context/AuthContext';
import { manualFactorSourceRef } from '../model/emissionFactorForm';
import { normalizeElectricitySuppliers } from '../../office/model/scope2Assignment';
import {
  canonicalSyncSourceScope,
  isActiveSyncStatus,
  isTerminalSyncStatus,
} from '../model/emissionFactorSyncRecovery';

const SYNC_STATUS_UNAVAILABLE = 'SYNC_STATUS_UNAVAILABLE';
const EF_SYNC_RESOURCE_NAMESPACE = 'all-data-input';
const USER_OPERATION_ORIGIN = 'USER';

const syncStorageNamespace = (authenticatedUserId) => {
  const stableUserId = String(authenticatedUserId || '').trim();
  if (!stableUserId) throw new Error('Authenticated user ID is required for EF sync.');
  return encodeURIComponent(stableUserId);
};

const syncStoragePrefix = (authenticatedUserId) => (
  `ef-sync:${syncStorageNamespace(authenticatedUserId)}:${EF_SYNC_RESOURCE_NAMESPACE}:`
);

const activeSyncStorageKey = (authenticatedUserId, fiscalYear, sourceScope) => (
  `${syncStoragePrefix(authenticatedUserId)}${Number(fiscalYear)}:${sourceScope}:active`
);

const pendingSyncStorageKey = (authenticatedUserId, fiscalYear, sourceScope) => (
  `${syncStoragePrefix(authenticatedUserId)}${Number(fiscalYear)}:${sourceScope}:pending`
);

// Reuse an ID only while one POST outcome is unknown; completed user actions get a new ID.
export const acquirePendingSyncIdempotencyKey = (
  authenticatedUserId,
  fiscalYear,
  sourceScope,
) => {
  const storageKey = pendingSyncStorageKey(authenticatedUserId, fiscalYear, sourceScope);
  let idempotencyKey = sessionStorage.getItem(storageKey);
  if (!idempotencyKey) {
    idempotencyKey = createIdempotencyKey();
    sessionStorage.setItem(storageKey, idempotencyKey);
  }
  return idempotencyKey;
};

// Release request identity after the server gives a deterministic HTTP outcome.
export const releasePendingSyncIdempotencyKey = (
  authenticatedUserId,
  fiscalYear,
  sourceScope,
) => {
  sessionStorage.removeItem(pendingSyncStorageKey(authenticatedUserId, fiscalYear, sourceScope));
};

const operationResult = (operation) => ({
  ...(operation.summary || {}),
  ...operation,
  sources: operation.summary?.sources || operation.sources || [],
  token_usage: operation.token_usage,
});

const DEFAULT_FILTERS = {
  search: '',
  scope: FILTER_ALL,
  activity: '',
  factorValue: '',
  unit: FILTER_ALL,
  sourceFile: FILTER_ALL,
  fiscalYear: FILTER_ALL,
  origin: FILTER_ALL,
  status: 'active',
};

const scopeOrder = { [SCOPE.SCOPE_1]: 1, [SCOPE.SCOPE_2]: 2, [SCOPE.SCOPE_3]: 3 };
const isAllFilter = (value) => value === FILTER_ALL || String(value).toLowerCase() === 'all';

const optionList = (values) => [...new Set(values.filter((value) => value !== null && value !== undefined && value !== ''))]
  .sort((left, right) => String(left).localeCompare(String(right)))
  .map((value) => ({ value, label: String(value) }));

// Adapt the backend source shape to the existing table/view model.
const normalizeFactor = (factor) => ({
  ...factor,
  ef_name: factor.business_name || factor.ef_name,
  ef_value: String(factor.factor_value ?? factor.ef_value ?? ''),
  ef_unit: factor.factor_unit || factor.ef_unit,
  fiscal_year: factor.effective_year ?? factor.fiscal_year,
  ef_external_id: factor.source_external_id || factor.ef_external_id,
  ef_company_core: factor.ef_company_core,
  supplier_id: factor.supplier_id || factor.supplier_ref_id || null,
  registration_no: factor.registration_no || null,
  source_year: Number(factor.source_year || factor.effective_year || factor.fiscal_year),
  application_mode: factor.application_mode || null,
  is_carried_forward: Boolean(factor.is_carried_forward),
  carried_from_fiscal_year: factor.carried_from_fiscal_year || null,
  category_label: CATEGORY_META[factor.category]?.label || factor.category,
  source_file: factor.source_file
    ? {
      ...factor.source_file,
      name: factor.source_file.original_filename || factor.source_file.file_name,
    }
    : null,
  updated_at: factor.updated_at || factor.created_at,
});

export default function useEmissionFactorMaster() {
  const { auth } = useAuthContext();
  const authenticatedUserId = getAuthenticatedUserId(auth);
  const [factors, setFactors] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierLoadError, setSupplierLoadError] = useState(null);
  const [historyByFactor, setHistoryByFactor] = useState({});
  const [syncHistory, setSyncHistory] = useState([]);
  const [syncHistoryLoading, setSyncHistoryLoading] = useState(false);
  const [filters, setFiltersState] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPageState] = useState(25);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncOperation, setSyncOperation] = useState(null);
  const [syncMonitoringError, setSyncMonitoringError] = useState(null);
  const [syncPolicy, setSyncPolicy] = useState(null);
  const syncPollControllerRef = useRef(null);

  useEffect(() => {
    setSyncOperation(null);
    setSyncMonitoringError(null);
    setSyncing(false);
    return () => {
      syncPollControllerRef.current?.abort();
      syncPollControllerRef.current = null;
    };
  }, [authenticatedUserId]);

  // Refresh the authoritative list after create, correction, and Agent sync.
  const loadFactors = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listEmissionFactors({ status: filters.status });
      setFactors((response || []).map(normalizeFactor));
      setLoadError(null);
    } catch (error) {
      setLoadError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [filters.status]);

  const loadSuppliers = useCallback(async () => {
    try {
      setSuppliers(normalizeElectricitySuppliers(await listElectricitySuppliers()));
      setSupplierLoadError(null);
    } catch (error) {
      setSupplierLoadError(error);
    }
  }, []);

  useEffect(() => {
    loadFactors().catch(() => {});
    loadSuppliers();
    getEmissionFactorSyncPolicy().then(setSyncPolicy).catch(() => {});
  }, [loadFactors, loadSuppliers]);

  const setFilter = useCallback((key, value) => {
    setFiltersState((previous) => ({ ...previous, [key]: value }));
    setPage(0);
  }, []);

  const setRowsPerPage = useCallback((value) => {
    setRowsPerPageState(value);
    setPage(0);
  }, []);

  const options = useMemo(() => {
    return {
      units: optionList(factors.map((factor) => factor.ef_unit)),
      sourceFiles: optionList(factors.map((factor) => factor.source_file?.name)),
      fiscalYears: optionList([
        ...factors.map((factor) => factor.fiscal_year),
        ...managementReportingYearOptions({ now: new Date(), yearsAhead: 1 }),
      ]).sort((left, right) => Number(right.value) - Number(left.value)),
      origins: [
        { value: 'MOE_SOURCE', label: 'MOE_SOURCE' },
        { value: 'BUSINESS_POLICY', label: 'BUSINESS_POLICY' },
        { value: 'MANUAL', label: 'MANUAL' },
      ],
    };
  }, [factors]);

  const visibleFactors = useMemo(() => factors
    .filter((factor) => filters.scope === FILTER_ALL || factor.scope === filters.scope)
    .filter((factor) => matchesSearch(
      filters.activity,
      activityDisplayName(factor),
      factor.ef_name,
      factor.ef_external_id,
      factor.category_label,
    ))
    .filter((factor) => matchesSearch(filters.factorValue, String(factor.ef_value)))
    .filter((factor) => filters.unit === FILTER_ALL || factor.ef_unit === filters.unit)
    .filter((factor) => filters.sourceFile === FILTER_ALL || factor.source_file?.name === filters.sourceFile)
    .filter((factor) => isAllFilter(filters.fiscalYear)
      || factor.fiscal_year === Number(filters.fiscalYear))
    .filter((factor) => filters.origin === FILTER_ALL || factor.value_origin === filters.origin)
    .filter((factor) => matchesSearch(
      filters.search,
      activityDisplayName(factor),
      factor.ef_name,
      factor.ef_external_id,
      factor.category_label,
      CATEGORY_META[factor.category]?.short,
      factor.ef_unit,
      factor.source_file?.name,
      factor.fiscal_year,
    ))
    .sort((left, right) => (
      (scopeOrder[left.scope] - scopeOrder[right.scope])
      || left.category.localeCompare(right.category)
      || activityDisplayName(left).localeCompare(activityDisplayName(right))
    )), [factors, filters]);

  const saveFactor = useCallback(async (existing, body) => {
    setSaving(true);
    try {
      const { source_file: sourceFile, ...factorPayload } = body;
      let uploadedSource = null;
      if (sourceFile) {
        uploadedSource = await uploadSourceFile(sourceFile, {
          fiscalYear: body.effective_year,
          sourceType: body.scope === SCOPE.SCOPE_2 ? 'DENKI' : 'DB_V3',
        });
      }
      const sourceRefId = manualFactorSourceRef(existing, uploadedSource);
      const clientOwnedItemKey = factorPayload.scope === SCOPE.SCOPE_2
        ? {}
        : { item_key: existing?.item_key || factorPayload.item_key || null };
      const request = {
        ...factorPayload,
        ...clientOwnedItemKey,
        source_ref_id: sourceRefId,
        source_locator: sourceRefId && !uploadedSource
          ? existing?.source_locator || null
          : null,
      };
      const response = existing
        ? await publishCorrection(existing.id, request, existing.etag)
        : await createEmissionFactor(request);
      const factor = response.factor || response;
      let listRefreshFailed = false;
      try {
        await loadFactors();
      } catch (_loadError) {
        // The factor mutation succeeded; loadFactors exposes refresh failure separately.
        listRefreshFailed = true;
      }
      return { factor: normalizeFactor(factor), listRefreshFailed };
    } finally {
      setSaving(false);
    }
  }, [loadFactors]);

  const updateFactorStatus = useCallback(async (factor, status, reason) => {
    const response = await updateEmissionFactorStatus(
      factor.id,
      status,
      reason,
      factor.etag,
    );
    const updatedFactor = normalizeFactor(response.factor || response);
    try {
      await loadFactors();
    } catch (_loadError) {
      // The status mutation succeeded; loadFactors exposes refresh failure separately.
      return { factor: updatedFactor, listRefreshFailed: true };
    }
    return { factor: updatedFactor, listRefreshFailed: false };
  }, [loadFactors]);

  const pollSyncOperation = useCallback(async ({
    operationId, fiscalYear, statusUrl, sourceScope, signal, initialOperation,
  }) => {
    const result = await pollKnownOperationStatus({
      operationId,
      initialOperation,
      fetchStatus: async ({ signal: requestSignal }) => operationResult(
        await getEmissionFactorSyncStatus(statusUrl, operationId, {
          signal: requestSignal,
        }),
      ),
      isActiveStatus: isActiveSyncStatus,
      isTerminalStatus: isTerminalSyncStatus,
      onStatus: (operation) => {
        setSyncOperation(operation);
        setSyncMonitoringError(null);
      },
      signal,
      monitoringErrorCode: SYNC_STATUS_UNAVAILABLE,
      monitoringErrorContext: { fiscalYear, statusUrl, sourceScope },
    });
    if (!result || signal?.aborted) return null;

    sessionStorage.removeItem(activeSyncStorageKey(authenticatedUserId, fiscalYear, sourceScope));
    releasePendingSyncIdempotencyKey(authenticatedUserId, fiscalYear, sourceScope);
    if (result.status !== 'FAILED') {
      try {
        await loadFactors();
      } catch (_loadError) {
        // loadFactors exposes refresh failures separately from the sync outcome.
      }
    }
    return result;
  }, [authenticatedUserId, loadFactors]);

  const startSyncStatusMonitoring = useCallback(async ({
    operationId, fiscalYear, statusUrl, sourceScope, initialOperation = null,
  }) => {
    const controller = new AbortController();
    syncPollControllerRef.current?.abort();
    syncPollControllerRef.current = controller;
    setSyncing(true);
    setSyncMonitoringError(null);
    try {
      const knownOperation = initialOperation || {
        operation_id: operationId,
        fiscal_year: fiscalYear,
        status: 'QUEUED',
        status_url: statusUrl,
      };
      return await pollSyncOperation({
        operationId,
        fiscalYear,
        statusUrl,
        sourceScope,
        signal: controller.signal,
        initialOperation: knownOperation,
      });
    } catch (error) {
      if (!error?.isMonitoringError) throw error;
      setSyncMonitoringError({
        code: error.code,
        operationId,
        fiscalYear,
        statusUrl,
        sourceScope,
        operation: error.operation,
      });
      return null;
    } finally {
      if (syncPollControllerRef.current === controller) {
        syncPollControllerRef.current = null;
      }
      if (!controller.signal.aborted) setSyncing(false);
    }
  }, [pollSyncOperation]);

  useEffect(() => {
    if (!authenticatedUserId || !syncPolicy) return undefined;
    const allowedFiscalYears = new Set(
      (syncPolicy.allowed_fiscal_years || []).map((option) => Number(option.fiscal_year)),
    );
    if (allowedFiscalYears.size === 0) return undefined;
    const prefix = syncStoragePrefix(authenticatedUserId);
    const recoverableOperations = [];
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(prefix) && key.endsWith(':active'))
      .forEach((key) => {
        let record;
        try {
          record = JSON.parse(sessionStorage.getItem(key));
        } catch (_error) {
          record = null;
        }
        const isRecoverable = Boolean(record?.operationId)
          && allowedFiscalYears.has(Number(record.fiscalYear))
          && record.origin === USER_OPERATION_ORIGIN
          && record.requestedByUserId === authenticatedUserId;
        if (!isRecoverable) {
          sessionStorage.removeItem(key);
          return;
        }
        try {
          requireEfSyncStatusUrl(record.statusUrl, record.operationId);
          recoverableOperations.push(record);
        } catch (_error) {
          sessionStorage.removeItem(key);
        }
      });
    if (recoverableOperations.length === 0) return undefined;
    const mostRecent = recoverableOperations.reduce((latest, record) => (
      Date.parse(record.startedAt) > Date.parse(latest.startedAt) ? record : latest
    ));
    startSyncStatusMonitoring(mostRecent).catch(() => {});
    return undefined;
  }, [
    authenticatedUserId,
    syncPolicy,
    startSyncStatusMonitoring,
  ]);

  const syncFactors = useCallback(async (fiscalYear, options = {}) => {
    const sourceTypes = options.sourceTypes || null;
    const sourceScope = canonicalSyncSourceScope(sourceTypes);
    const controller = new AbortController();
    syncPollControllerRef.current?.abort();
    syncPollControllerRef.current = controller;
    setSyncing(true);
    setSyncMonitoringError(null);
    try {
      const idempotencyKey = acquirePendingSyncIdempotencyKey(
        authenticatedUserId,
        fiscalYear,
        sourceScope,
      );
      let accepted;
      try {
        accepted = await syncEmissionFactors(
          {
            force: options.force,
            fiscalYear,
            sourceTypes,
          },
          idempotencyKey,
        );
        releasePendingSyncIdempotencyKey(authenticatedUserId, fiscalYear, sourceScope);
      } catch (error) {
        if (Number(error?.status) > 0) {
          releasePendingSyncIdempotencyKey(authenticatedUserId, fiscalYear, sourceScope);
        }
        throw error;
      }
      const acceptedFiscalYear = Number(accepted.fiscal_year ?? fiscalYear);
      const acceptedStatusUrl = requireEfSyncStatusUrl(
        accepted.status_url,
        accepted.operation_id,
      );
      const acceptedSyncKey = activeSyncStorageKey(
        authenticatedUserId,
        acceptedFiscalYear,
        sourceScope,
      );
      sessionStorage.setItem(acceptedSyncKey, JSON.stringify({
        operationId: accepted.operation_id,
        fiscalYear: acceptedFiscalYear,
        sourceTypes,
        startedAt: new Date().toISOString(),
        statusUrl: acceptedStatusUrl,
        origin: USER_OPERATION_ORIGIN,
        requestedByUserId: authenticatedUserId,
      }));
      const acceptedResult = operationResult(accepted);
      if (controller.signal.aborted) return acceptedResult;
      setSyncOperation(acceptedResult);
      const completedResult = await startSyncStatusMonitoring({
        operationId: accepted.operation_id,
        fiscalYear: acceptedFiscalYear,
        statusUrl: acceptedStatusUrl,
        sourceScope,
        initialOperation: acceptedResult,
      });
      return completedResult || acceptedResult;
    } finally {
      if (syncPollControllerRef.current === controller) {
        syncPollControllerRef.current = null;
      }
      if (!controller.signal.aborted) setSyncing(false);
    }
  }, [authenticatedUserId, startSyncStatusMonitoring]);

  const retrySyncStatus = useCallback(async () => {
    const operationId = syncMonitoringError?.operationId;
    const fiscalYear = syncMonitoringError?.fiscalYear;
    if (!operationId || !fiscalYear) return null;
    return startSyncStatusMonitoring({
      operationId,
      fiscalYear,
      statusUrl: syncMonitoringError.statusUrl,
      sourceScope: syncMonitoringError.sourceScope,
      initialOperation: syncMonitoringError.operation || syncOperation,
    });
  }, [startSyncStatusMonitoring, syncMonitoringError, syncOperation]);

  const downloadSource = useCallback(async (factorOrSource) => {
    const source = factorOrSource?.source_file || factorOrSource;
    if (!source?.id) throw new Error('No source workbook is linked to this factor.');
    const blob = await downloadSourceFile(source.id);
    const url = URL.createObjectURL(blob);
    try {
      triggerBrowserDownload({
        href: url,
        fileName: source.name || 'source.xlsx',
      });
    } finally {
      URL.revokeObjectURL(url);
    }
  }, []);

  // History is loaded lazily because each correction chain is a separate query.
  const loadHistory = useCallback(async (factor) => {
    if (!factor?.id) return [];
    const entries = await getEmissionFactorHistory(factor.id);
    setHistoryByFactor((previous) => ({ ...previous, [factor.id]: entries || [] }));
    return entries || [];
  }, []);

  const historyFor = useCallback(
    (factor) => historyByFactor[factor?.id] || [],
    [historyByFactor],
  );

  const loadSyncHistory = useCallback(async () => {
    setSyncHistoryLoading(true);
    try {
      const entries = await listEmissionFactorSyncHistory();
      setSyncHistory(entries || []);
      return entries || [];
    } finally {
      setSyncHistoryLoading(false);
    }
  }, []);

  return {
    factors: visibleFactors,
    suppliers,
    supplierLoadError,
    reloadSuppliers: loadSuppliers,
    filters,
    setFilter,
    options,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    loading,
    loadError,
    reload: loadFactors,
    saving,
    syncing,
    syncOperation,
    syncMonitoringError,
    syncPolicy,
    saveFactor,
    updateFactorStatus,
    syncFactors,
    retrySyncStatus,
    downloadSource,
    loadHistory,
    historyFor,
    syncHistory,
    syncHistoryLoading,
    loadSyncHistory,
  };
}
